import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, Star, Clock, ArrowLeft } from "lucide-react";
import { api, resolveMedia } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { WatchlistButton } from "@/components/WatchlistButton";
import { RelatedAnime } from "@/components/RelatedAnime";
import { AdUnit } from "@/components/AdUnit";
import { SeasonTabs, groupSeasons } from "@/components/SeasonTabs";
import { watchPath } from "@/lib/progress";

export default function AnimeDetail() {
  const { id } = useParams();
  const [anime, setAnime] = useState(null);
  const [err, setErr] = useState(false);
  const [season, setSeason] = useState(1);

  useEffect(() => {
    api.get(`/anime/${id}`).then((r) => { setAnime(r.data); setSeason(groupSeasons(r.data.episodes)[0] || 1); }).catch(() => setErr(true));
  }, [id]);

  if (err) return <div className="pt-40 text-center font-display">Anime not found.</div>;
  if (!anime) return <div className="min-h-screen" />;
  const seasons = groupSeasons(anime.episodes);
  const eps = anime.episodes.filter((e) => (e.season || 1) === season);
  const first = anime.episodes[0];

  return (
    <main data-testid="anime-detail-page" className="min-h-screen">
      <div className="relative h-[60vh] min-h-[420px] overflow-hidden">
        <motion.img initial={{ scale: 1.15, opacity: 0 }} animate={{ scale: 1, opacity: 0.5 }} transition={{ duration: 1.4, ease: [0.2, 0.8, 0.2, 1] }} src={resolveMedia(anime.banner_url || anime.cover_url)} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#08070C] via-[#08070C]/60 to-transparent" />
        <div className="absolute inset-0 kp-scanlines" />
        <Link to="/browse" data-testid="detail-back-link" className="absolute top-28 left-5 sm:left-8 kp-eyebrow flex items-center gap-2 hover:!text-white transition-colors">
          <ArrowLeft size={14} /> Back
        </Link>
      </div>

      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 -mt-48 relative grid grid-cols-1 md:grid-cols-12 gap-10">
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.2, ease: [0.2, 0.8, 0.2, 1] }} className="md:col-span-3">
          <div className="aspect-[3/4] kp-card-clip overflow-hidden border border-[#FF2A55]/40 kp-glow-crimson">
            <img src={resolveMedia(anime.cover_url)} alt={anime.title} className="w-full h-full object-cover" />
          </div>
        </motion.div>

        <div className="md:col-span-9 flex flex-col gap-6 md:pt-24">
          <Reveal>
            <div className="flex flex-wrap gap-2 mb-4">
              {anime.genres.map((g) => (
                <span key={g} className="kp-pill cursor-default">{g}</span>
              ))}
            </div>
            <h1 data-testid="detail-title" className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]">{anime.title}</h1>
            <div className="flex flex-wrap items-center gap-6 mt-5 text-xs font-mono text-[#94A3B8]">
              <span className="flex items-center gap-1.5"><Star size={12} className="text-[#00F0FF] fill-[#00F0FF]" /> {anime.rating ?? "—"}</span>
              <span>{anime.year}</span>
              <span className="text-[#FF2A55]">{anime.status?.toUpperCase()}</span>
              <span>{anime.episodes.length} EPISODES{seasons.length > 1 ? ` · ${seasons.length} SEASONS` : ""}</span>
            </div>
            <p className="text-[#94A3B8] leading-relaxed mt-6 max-w-2xl text-base">{anime.synopsis}</p>
            <div className="flex flex-wrap gap-3 mt-8">
              {first && (
                <Link to={watchPath(anime.id, first.number, first.season)} data-testid={`play-episode-button-${first.id}`} className="kp-btn kp-btn-primary">
                  <Play size={14} className="fill-current" /> {seasons.length > 1 ? `S${first.season} ` : ""}Watch Episode {first.number}
                </Link>
              )}
              <WatchlistButton anime={anime} size="lg" />
            </div>
          </Reveal>

          <Reveal delay={0.1} className="mt-10">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <span className="kp-eyebrow">Episodes{seasons.length > 1 ? ` · Season ${season}` : ""}</span>
              <SeasonTabs seasons={seasons} active={season} onChange={setSeason} />
            </div>
            <div data-testid="episode-list" className="mt-5 flex flex-col divide-y divide-white/[0.06] border-y border-white/[0.06]">
              {eps.length === 0 && <p className="py-8 text-[#64748B] text-sm">No episodes uploaded yet.</p>}
              {eps.map((ep) => (
                <Link key={ep.id} to={watchPath(anime.id, ep.number, ep.season)} data-testid={`episode-row-${ep.id}`} className="group flex items-center gap-6 py-5 hover:bg-white/[0.02] transition-colors px-2 -mx-2">
                  <span className="font-display font-black text-2xl kp-outline-text group-hover:text-[#FF2A55] transition-colors w-14">{String(ep.number).padStart(2, "0")}</span>
                  <div className="relative w-28 sm:w-36 aspect-video shrink-0 overflow-hidden kp-card-clip bg-[#0F0D16] border border-white/[0.06]">
                    <img src={resolveMedia(ep.thumbnail_url || anime.banner_url || anime.cover_url)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><Play size={16} className="fill-white text-white" /></span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-sm sm:text-base font-semibold truncate">{ep.title || `Episode ${ep.number}`}</div>
                    {ep.duration && <div className="text-xs text-[#64748B] font-mono mt-1 flex items-center gap-1"><Clock size={11} /> {ep.duration}</div>}
                  </div>
                  <span className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center group-hover:bg-[#FF2A55] group-hover:border-[#FF2A55] transition-colors">
                    <Play size={14} className="fill-current ml-0.5" />
                  </span>
                </Link>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
      <AdUnit testId="ad-unit-detail" className="mt-20" />
      <RelatedAnime animeId={anime.id} />
    </main>
  );
}
